import { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogTitle,
} from "./ui/dialog";
import { BrandMark } from "./BrandLogo";
import { useSession } from "@/hooks/useSession";
import { SsoButtons } from "./auth/SsoButtons";
import { SignInForm } from "./auth/SignInForm";
import { SignUpForm } from "./auth/SignUpForm";
import { OtpSendForm, OtpVerifyForm } from "./auth/OtpForms";
import { ResetForm } from "./auth/ResetForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Turnstile, TurnstileHandle, isTurnstileEnabled } from "./Turnstile";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AuthDialog({ open, onOpenChange }: Props) {
  const { signIn, signUp, sendOtp, verifyOtp, signInWithGoogle, signInWithGitHub, resetPassword, verifyResetPassword, checkEmailAuthMethod, supabaseEnabled } = useSession();
  const [tab, setTab] = useState<"signin" | "signup">("signin");
  const [mode, setMode] = useState<"credentials" | "otp-send" | "otp-verify" | "reset">("credentials");
  const [otpEmail, setOtpEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const captchaTokenRef = useRef<string | null>(null);

  const onCaptchaToken = (token: string) => {
    captchaTokenRef.current = token;
  };

  async function waitForToken(timeoutMs = 8000): Promise<string | null> {
    if (captchaTokenRef.current) return captchaTokenRef.current;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      await new Promise(r => setTimeout(r, 200));
      if (captchaTokenRef.current) return captchaTokenRef.current;
    }
    return null;
  }

  function consumeToken() {
    captchaTokenRef.current = null;
    turnstileRef.current?.reset();
  }

  const reset = () => { setMsg(null); setErr(null); };

  const handleSignIn = async (email: string, password: string) => {
    reset(); setLoading(true);
    const token = await waitForToken();
    if (isTurnstileEnabled() && !token) { setLoading(false); setErr("CAPTCHA not ready. Please wait and try again."); return; }
    const res = await signIn(email, password, token ?? undefined);
    consumeToken();
    setLoading(false);
    if (res.error) setErr(res.error);
    else { setMsg("Signed in."); setTimeout(() => onOpenChange(false), 400); }
  };

  const handleSignUp = async (email: string, password: string) => {
    reset();
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setLoading(true);
    const token = await waitForToken();
    if (isTurnstileEnabled() && !token) { setLoading(false); setErr("CAPTCHA not ready. Please wait and try again."); return; }
    const emailCheck = await checkEmailAuthMethod(email);
    if (emailCheck.exists) { setLoading(false); setErr(emailCheck.message ?? "Email exists."); return; }
    const res = await signUp(email, password, token ?? undefined);
    consumeToken();
    setLoading(false);
    if (res.error) { setErr(res.error); return; }

    if (res.confirmationRequired) {
      // Account created but email not yet confirmed — route the user
      // straight into the OTP-verify step. The OTP-verify UI lives in
      // the "signin" tab's mode branch (the "signup" tab body only
      // renders <SignUpForm>), so we have to flip tab first before
      // setting mode, otherwise the user sees the empty signup form
      // and has no idea where to paste the code.
      setOtpEmail(email);
      setTab("signin");
      setMode("otp-verify");
      setMsg(res.message ?? "Account created. Enter the 6-digit code we just emailed you.");
    } else {
      setMsg(res.message ?? "Account created. Sign in.");
      setTab("signin");
    }
  };

  const handleSendOtp = async (email: string) => {
    reset(); setOtpEmail(email);
    setLoading(true);
    const token = await waitForToken();
    if (isTurnstileEnabled() && !token) { setLoading(false); setErr("CAPTCHA not ready. Please wait and try again."); return; }
    const res = await sendOtp(email, false, token ?? undefined);
    consumeToken();
    setLoading(false);
    if (res.error) setErr(res.error);
    else { setMsg("Code sent."); setMode("otp-verify"); }
  };

  const handleVerifyOtp = async (token: string) => {
    reset(); setLoading(true);
    const res = await verifyOtp(otpEmail, token);
    setLoading(false);
    if (res.error) setErr(res.error);
    else { setMsg("Verified."); setTimeout(() => onOpenChange(false), 400); }
  };

  const handleGoogle = async () => { reset(); setLoading(true); const r = await signInWithGoogle(); setLoading(false); if (r.error) setErr(r.error); };
  const handleGitHub = async () => { reset(); setLoading(true); const r = await signInWithGitHub(); setLoading(false); if (r.error) setErr(r.error); };

  const handleSendReset = async () => {
    reset();
    setLoading(true);
    const token = await waitForToken();
    if (isTurnstileEnabled() && !token) { setLoading(false); setErr("CAPTCHA not ready. Please wait and try again."); return; }
    const res = await resetPassword(otpEmail, token ?? undefined);
    consumeToken();
    setLoading(false);
    if (res.error) setErr(res.error);
    else setMsg("Recovery code sent.");
  };

  const handleVerifyReset = async (token: string, newPassword: string) => {
    reset(); setLoading(true);
    const res = await verifyResetPassword(otpEmail, token, newPassword);
    setLoading(false);
    if (res.error) setErr(res.error);
    else { setMsg("Password reset."); setTimeout(() => { setTab("signin"); setMode("credentials"); reset(); }, 1500); }
  };

  if (!supabaseEnabled) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="border-l-[3px] border-l-destructive">
          <DialogTitle className="font-display">Sign-in unavailable</DialogTitle>
          <DialogDescription className="text-muted-foreground">Auth backend not configured.</DialogDescription>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card/95 backdrop-blur-xl border-border/50 overflow-hidden p-0">
        <div className="h-1 bg-gradient-to-r from-primary via-accent to-primary" />
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <BrandMark className="h-7 w-7" />
            <DialogTitle className="font-display font-bold text-xl tracking-tight text-foreground">Welcome to Hideaway</DialogTitle>
          </div>
          <DialogDescription className="font-mono text-xs text-muted-foreground">Sign in to access all steganography tools.</DialogDescription>
          {isTurnstileEnabled() && (
            <div className="space-y-1">
              <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Security check</p>
              <Turnstile ref={turnstileRef} onToken={onCaptchaToken} onExpire={() => { captchaTokenRef.current = null; }} className="mt-1" />
            </div>
          )}
          <Tabs value={tab} onValueChange={(v) => { setTab(v as "signin" | "signup"); setMode("credentials"); reset(); }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin" className="font-mono text-xs">Sign in</TabsTrigger>
              <TabsTrigger value="signup" className="font-mono text-xs">Sign up</TabsTrigger>
            </TabsList>
            <TabsContent value="signin" className="space-y-3 pt-2">
              {mode === "credentials" ? (
                <>
                  <SignInForm onSignIn={handleSignIn} onSwitchToOtp={() => { setMode("otp-send"); reset(); }} onSwitchToReset={() => { setOtpEmail(""); setMode("reset"); reset(); }} loading={loading} error={err} />
                  <div className="relative"><div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border/50" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground font-mono">or</span></div></div>
                  <SsoButtons onGoogle={handleGoogle} onGitHub={handleGitHub} loading={loading} />
                </>
              ) : mode === "otp-send" ? (
                <OtpSendForm onSendOtp={handleSendOtp} onBack={() => { setMode("credentials"); reset(); }} loading={loading} />
              ) : mode === "otp-verify" ? (
                <div className="space-y-3">
                  <OtpVerifyForm email={otpEmail} onVerify={handleVerifyOtp} loading={loading} />
                  <button type="button" onClick={() => handleSendOtp(otpEmail)} className="text-xs text-muted-foreground hover:text-foreground font-mono">Resend code</button>
                </div>
              ) : mode === "reset" ? (
                <ResetForm email={otpEmail} onSendResetCode={handleSendReset} onVerifyReset={handleVerifyReset} onBack={() => { setMode("credentials"); reset(); }} loading={loading} error={err} success={msg} />
              ) : null}
              {msg && <p className="text-sm text-success font-mono">{msg}</p>}
              {err && (mode !== "credentials") && <p className="text-sm text-destructive font-mono" role="alert">{err}</p>}
            </TabsContent>
            <TabsContent value="signup" className="space-y-3 pt-2">
              <SignUpForm onSignUp={handleSignUp} loading={loading} error={err} />
              {msg && <p className="text-sm text-success font-mono">{msg}</p>}
              <div className="relative"><div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border/50" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground font-mono">or</span></div></div>
              <SsoButtons onGoogle={handleGoogle} onGitHub={handleGitHub} loading={loading} />
            </TabsContent>
          </Tabs>
          <p className="text-center font-mono text-[11px] text-muted-foreground">
            By continuing, you agree to our{" "}<a href="/terms" className="text-primary hover:underline">Terms</a>{" "}and{" "}<a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
